/**
 * Centralized strategy matching logic
 * Used by both Holdings and P&L views
 */

import type { ParsedOption } from './brokerage.types.ts';

// Maximum days apart for trades to be considered part of the same strategy
export const STRATEGY_WINDOW_DAYS = 3;

/**
 * Parse OCC option symbol to extract details
 * Format: "NVDA  260515P00190000" = NVDA, 2026-05-15, PUT, $190
 */
export function parseOptionSymbol(occSymbol: string): ParsedOption | null {
  if (!occSymbol) return null;

  const trimmed = occSymbol.trim().replace(/^O:/, '').replace(/\s+/g, '');
  if (trimmed.length < 15) return null;

  const strikeStr = trimmed.slice(-8);
  const typeChar = trimmed.slice(-9, -8);
  const dateStr = trimmed.slice(-15, -9);
  const underlying = trimmed.slice(0, -15).trim();

  if (!underlying || !/^\d{6}$/.test(dateStr) || !['C', 'P'].includes(typeChar)) {
    return null;
  }

  const year = 2000 + parseInt(dateStr.slice(0, 2), 10);
  const month = parseInt(dateStr.slice(2, 4), 10);
  const day = parseInt(dateStr.slice(4, 6), 10);
  const expiration = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const expDate = new Date(year, month - 1, day);
  const expirationDisplay = expDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit',
  });

  const strike = parseInt(strikeStr, 10) / 1000;

  return {
    underlying,
    expiration,
    expirationDisplay,
    type: typeChar === 'C' ? 'CALL' : 'PUT',
    strike,
  };
}

/**
 * Generic leg interface for strategy identification
 */
export interface StrategyLegInfo {
  parsed: ParsedOption | null;
  direction: 'long' | 'short';
  quantity: number;
}

/**
 * Identify strategy type based on legs
 * Supports: verticals, diagonals, calendars, covered calls, PMCC, straddles, strangles, iron condors
 */
export function identifyStrategyType(legs: StrategyLegInfo[], hasStock: boolean = false): string {
  const optionLegs = legs.filter(l => l.parsed);

  // Covered call: long stock + short call
  if (hasStock) {
    const shortCalls = optionLegs.filter(l => l.parsed?.type === 'CALL' && l.direction === 'short');
    if (shortCalls.length > 0) {
      // Check if it's a Poor Man's Covered Call (long call instead of stock)
      const longCalls = optionLegs.filter(l => l.parsed?.type === 'CALL' && l.direction === 'long');
      if (longCalls.length > 0) {
        return "Poor Man's Covered Call";
      }
      return 'Covered Call';
    }
    const shortPuts = optionLegs.filter(l => l.parsed?.type === 'PUT' && l.direction === 'short');
    if (shortPuts.length > 0) {
      return 'Cash-Secured Put';
    }
    return 'Stock Position';
  }

  if (optionLegs.length === 0) {
    return 'Stock Position';
  }

  if (optionLegs.length === 1) {
    const leg = optionLegs[0];
    const typeStr = leg.parsed?.type === 'CALL' ? 'Call' : 'Put';
    return leg.direction === 'long' ? `Long ${typeStr}` : `Short ${typeStr}`;
  }

  if (optionLegs.length === 2) {
    const types = optionLegs.map(l => l.parsed?.type || '').sort();
    const directions = optionLegs.map(l => l.direction || '').sort();
    const expirations = optionLegs.map(l => l.parsed?.expiration);
    const strikes = optionLegs.map(l => l.parsed?.strike ?? 0).sort((a, b) => a - b);

    const sameExpiration = expirations[0] === expirations[1];
    const sameStrike = strikes[0] === strikes[1];
    const sameType = types[0] === types[1];
    const oppositeDirections = directions[0] !== directions[1];

    // Poor Man's Covered Call: long deep ITM call + short OTM call
    if (sameType && types[0] === 'CALL' && oppositeDirections && !sameExpiration) {
      const longCall = optionLegs.find(l => l.direction === 'long');
      const shortCall = optionLegs.find(l => l.direction === 'short');
      if (longCall && shortCall) {
        const longExp = new Date(longCall.parsed!.expiration);
        const shortExp = new Date(shortCall.parsed!.expiration);
        if (longExp > shortExp) {
          return "Poor Man's Covered Call";
        }
      }
      return 'Call Diagonal';
    }

    // Put diagonal (including Poor Man's Covered Put)
    if (sameType && types[0] === 'PUT' && oppositeDirections && !sameExpiration) {
      return 'Put Diagonal';
    }

    // Calendar spread: same strike, same type, different expirations
    if (sameStrike && sameType && !sameExpiration && oppositeDirections) {
      return types[0] === 'CALL' ? 'Call Calendar' : 'Put Calendar';
    }

    // Vertical spread: same expiration, different strikes, same type
    if (sameExpiration && !sameStrike && sameType && oppositeDirections) {
      return types[0] === 'CALL' ? 'Call Spread' : 'Put Spread';
    }

    // Straddle: same strike, different types
    if (sameStrike && !sameType) {
      const allLong = directions.every(d => d === 'long');
      const allShort = directions.every(d => d === 'short');
      if (allLong) return 'Long Straddle';
      if (allShort) return 'Short Straddle';
    }

    // Strangle: different strikes, different types
    if (!sameStrike && !sameType) {
      const allLong = directions.every(d => d === 'long');
      const allShort = directions.every(d => d === 'short');
      if (allLong) return 'Long Strangle';
      if (allShort) return 'Short Strangle';
    }
  }

  if (optionLegs.length === 4) {
    const types = optionLegs.map(l => l.parsed?.type);
    const calls = types.filter(t => t === 'CALL').length;
    const puts = types.filter(t => t === 'PUT').length;

    if (calls === 2 && puts === 2) {
      return 'Iron Condor';
    }
    if (calls === 4 || puts === 4) {
      return 'Butterfly';
    }
  }

  return `${optionLegs.length}-Leg Strategy`;
}

/**
 * Check if two date ranges overlap within the strategy window
 */
export function datesOverlap(
  open1: string,
  close1: string | null,
  open2: string,
  close2: string | null
): boolean {
  const o1 = new Date(open1).getTime();
  const c1 = close1 ? new Date(close1).getTime() : Date.now();
  const o2 = new Date(open2).getTime();
  const c2 = close2 ? new Date(close2).getTime() : Date.now();

  const windowMs = STRATEGY_WINDOW_DAYS * 24 * 60 * 60 * 1000;

  // Check if open dates are within window of each other
  if (Math.abs(o1 - o2) <= windowMs) return true;

  // Check if close dates are within window of each other (if both closed)
  if (close1 && close2 && Math.abs(c1 - c2) <= windowMs) return true;

  return false;
}

/**
 * Generic item for matching - used by both holdings and trades
 */
export interface MatchableItem {
  id: string;
  underlying: string;
  optionSymbol: string | null;
  parsed: ParsedOption | null;
  direction: 'long' | 'short';
  quantity: number;
  openDate: string;
  closeDate: string | null;
}

/**
 * Match items into strategy groups
 * Groups by: same underlying, same option type, opposite direction, same quantity
 * Works across different expirations (for diagonals/calendars)
 */
export function matchIntoStrategyGroups<T extends MatchableItem>(
  items: T[]
): { matched: T[][]; unmatched: T[] } {
  const matched: T[][] = [];
  const usedIds = new Set<string>();

  // Sort by underlying for efficient grouping
  const byUnderlying = new Map<string, T[]>();
  for (const item of items) {
    const existing = byUnderlying.get(item.underlying) || [];
    existing.push(item);
    byUnderlying.set(item.underlying, existing);
  }

  for (const [, underlyingItems] of byUnderlying) {
    // Try to match diagonal/calendar spreads first (same type, opposite direction, different expiration)
    for (let i = 0; i < underlyingItems.length; i++) {
      if (usedIds.has(underlyingItems[i].id)) continue;
      const item1 = underlyingItems[i];
      if (!item1.parsed) continue;

      for (let j = i + 1; j < underlyingItems.length; j++) {
        if (usedIds.has(underlyingItems[j].id)) continue;
        const item2 = underlyingItems[j];
        if (!item2.parsed) continue;

        const sameType = item1.parsed.type === item2.parsed.type;
        const oppositeDirection = item1.direction !== item2.direction;
        const sameQuantity = item1.quantity === item2.quantity;
        const differentExpiration = item1.parsed.expiration !== item2.parsed.expiration;

        // Match diagonals/calendars: same type, opposite direction, same quantity, different expiration
        if (sameType && oppositeDirection && sameQuantity && differentExpiration) {
          matched.push([item1, item2]);
          usedIds.add(item1.id);
          usedIds.add(item2.id);
          break;
        }
      }
    }

    // Now try to match verticals (same expiration)
    for (let i = 0; i < underlyingItems.length; i++) {
      if (usedIds.has(underlyingItems[i].id)) continue;
      const item1 = underlyingItems[i];
      if (!item1.parsed) continue;

      for (let j = i + 1; j < underlyingItems.length; j++) {
        if (usedIds.has(underlyingItems[j].id)) continue;
        const item2 = underlyingItems[j];
        if (!item2.parsed) continue;

        const sameType = item1.parsed.type === item2.parsed.type;
        const oppositeDirection = item1.direction !== item2.direction;
        const sameQuantity = item1.quantity === item2.quantity;
        const sameExpiration = item1.parsed.expiration === item2.parsed.expiration;
        const differentStrike = item1.parsed.strike !== item2.parsed.strike;

        // Match verticals: same type, same expiration, opposite direction, different strikes
        if (sameType && sameExpiration && oppositeDirection && sameQuantity && differentStrike) {
          matched.push([item1, item2]);
          usedIds.add(item1.id);
          usedIds.add(item2.id);
          break;
        }
      }
    }
  }

  // Collect unmatched items
  const unmatched = items.filter(item => !usedIds.has(item.id));

  return { matched, unmatched };
}

/**
 * Group unmatched items by expiration date
 */
export function groupByExpiration<T extends { parsed: ParsedOption | null }>(
  items: T[]
): Map<string, T[]> {
  const groups = new Map<string, T[]>();

  for (const item of items) {
    const exp = item.parsed?.expiration || 'unknown';
    const existing = groups.get(exp) || [];
    existing.push(item);
    groups.set(exp, existing);
  }

  return groups;
}
